import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { timeAgo } from "@/lib/meta";
import { Send, MessageCircle, Twitter, Megaphone, Loader2, Radio, Rocket, Flame } from "lucide-react";

const STATUS_META = {
  sent: { cls: "bg-pulp/15 text-pulp", label: "sent" },
  simulated: { cls: "bg-ai/15 text-ai", label: "simulated" },
  failed: { cls: "bg-danger/15 text-danger", label: "failed" },
  coming_soon: { cls: "bg-line text-dust", label: "coming soon" },
};

const platIcon = { telegram: Send, discord: MessageCircle, x: Twitter };

const Section = ({ title, desc, children }) => (
  <div className="rounded-2xl border border-line bg-surface p-5">
    <h3 className="text-sm font-semibold text-cream">{title}</h3>
    {desc && <p className="mt-0.5 text-xs text-dust">{desc}</p>}
    <div className="mt-4 space-y-3">{children}</div>
  </div>
);

export default function Broadcast() {
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [channels, setChannels] = useState({ telegram: true, discord: true, x: false });
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tgToken, setTgToken] = useState("");
  const [tgChat, setTgChat] = useState("");
  const [dcHook, setDcHook] = useState("");

  const load = () => {
    api.get("/broadcast/config").then(({ data }) => { setCfg(data); setTgChat(data.telegram.chat_id || ""); }).catch(() => {});
    api.get("/broadcast/history").then(({ data }) => setHistory(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!message.trim()) { toast.error("Write a message first."); return; }
    const chans = Object.keys(channels).filter((c) => channels[c]);
    if (!chans.length) { toast.error("Pick at least one channel."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/broadcast/send", { message, channels: chans });
      const sent = data.channels.filter((c) => c.status === "sent").length;
      const sim = data.channels.filter((c) => c.status === "simulated").length;
      toast.success(sent ? `Broadcast sent to ${sent} channel(s).` : `Broadcast queued (${sim} simulated — add creds to go live).`);
      setMessage("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed."); }
    finally { setSending(false); }
  };

  const saveCfg = async (patch) => {
    setSaving(true);
    try {
      await api.post("/broadcast/config", patch);
      toast.success("Channels saved.");
      setTgToken(""); setDcHook("");
      load();
    } catch (e) { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  const connectX = async () => {
    try {
      const { data } = await api.get("/auth/x/broadcast/authorize");
      if (!data.enabled) { toast.error("X isn't configured yet — add X_CLIENT_ID / X_CLIENT_SECRET / X_REDIRECT_URI to the backend .env."); return; }
      window.open(data.url, "_blank", "noopener");
      toast.message("Authorize Wen Moon on X in the new tab, then return here and refresh.");
    } catch (e) { toast.error("Could not start X connect."); }
  };

  if (!cfg) return <div className="p-8 text-dust">Loading broadcast center…</div>;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 lg:px-10" data-testid="broadcast-page">
      <div className="mb-6 flex items-center gap-3">
        <Megaphone className="text-juice" size={22} />
        <div>
          <h1 className="text-2xl font-bold text-cream">Broadcast</h1>
          <p className="text-sm text-dust">Push one message to every channel. Burns and launches auto-post.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.3fr_1fr]">
        <div className="space-y-5">
          {/* Composer */}
          <Section title="Compose broadcast" desc="Send to Telegram, Discord and X in one shot.">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="gm. $MOON just sent it to the moon…"
              rows={4}
              className="resize-none border-line bg-raised text-cream"
              data-testid="broadcast-message"
            />
            <div className="flex flex-wrap gap-2">
              {[["telegram", "Telegram", Send], ["discord", "Discord", MessageCircle], ["x", "X", Twitter]].map(([k, lbl, Icon]) => {
                const disabled = k === "x" && !cfg.x?.connected;
                const on = channels[k];
                return (
                  <button
                    key={k}
                    type="button"
                    disabled={disabled}
                    onClick={() => setChannels((c) => ({ ...c, [k]: !c[k] }))}
                    data-testid={`broadcast-toggle-${k}`}
                    title={disabled ? "Connect your X account below to post to X" : ""}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${disabled ? "cursor-not-allowed border-line/50 text-dust/40" : on ? "border-juice bg-juice/10 text-cream" : "border-line text-dust hover:bg-raised"}`}
                  >
                    <Icon size={13} /> {lbl}{k === "x" && !cfg.x?.connected ? " (connect)" : ""}
                  </button>
                );
              })}
            </div>
            <Button onClick={send} disabled={sending} className="w-full gap-2 bg-juice text-ink hover:bg-juice/90 font-semibold" data-testid="broadcast-send">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Radio size={16} />} Broadcast now
            </Button>
          </Section>

          {/* History */}
          <Section title="Recent broadcasts">
            <div className="space-y-3" data-testid="broadcast-history">
              {history.map((b) => (
                <div key={b.id} className="rounded-xl border border-line bg-raised/40 p-3">
                  <div className="mb-1.5 flex items-center gap-2 text-[11px] text-dust">
                    {b.source === "auto_burn" ? <Flame size={12} className="text-pulp" /> : b.source === "auto_launch" ? <Rocket size={12} className="text-juice" /> : <Radio size={12} className="text-ai" />}
                    <span className="uppercase tracking-wide">{b.source.replace("_", " ")}</span>
                    <span className="ml-auto">{timeAgo(b.created_at)}</span>
                  </div>
                  <p className="text-sm text-cream">{b.message}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {b.channels.map((c, i) => {
                      const st = STATUS_META[c.status] || STATUS_META.simulated;
                      const Icon = platIcon[c.platform] || Send;
                      return (
                        <span key={i} className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] uppercase ${st.cls}`}>
                          <Icon size={10} /> {c.platform} · {st.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
              {!history.length && <p className="text-sm text-dust">No broadcasts yet.</p>}
            </div>
          </Section>
        </div>

        {/* Channel setup */}
        <div className="space-y-5">
          <Section title="Telegram" desc={cfg.telegram.token_set ? "Bot token saved. Broadcasts go live." : "Simulated until a bot token + chat id are saved."}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-dust">Enabled</span>
              <Switch checked={cfg.telegram.enabled} onCheckedChange={(v) => saveCfg({ telegram: { enabled: v } })} data-testid="tg-enabled" />
            </div>
            <Input value={tgToken} onChange={(e) => setTgToken(e.target.value)} placeholder={cfg.telegram.token_set ? "•••••••• (saved) — paste to replace" : "Bot token from @BotFather"} className="border-line bg-raised font-mono text-sm" data-testid="tg-token" />
            <Input value={tgChat} onChange={(e) => setTgChat(e.target.value)} placeholder="Chat / channel ID (e.g. -1001234567890)" className="border-line bg-raised font-mono text-sm" data-testid="tg-chat" />
            <Button onClick={() => saveCfg({ telegram: { bot_token: tgToken, chat_id: tgChat, enabled: cfg.telegram.enabled } })} disabled={saving} variant="outline" size="sm" className="w-full border-line bg-raised hover:bg-line/40" data-testid="tg-save">Save Telegram</Button>
          </Section>

          <Section title="Discord" desc={cfg.discord.webhook_set ? `Webhook saved (${cfg.discord.webhook_hint}).` : "Simulated until a webhook URL is saved."}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-dust">Enabled</span>
              <Switch checked={cfg.discord.enabled} onCheckedChange={(v) => saveCfg({ discord: { enabled: v } })} data-testid="dc-enabled" />
            </div>
            <Input value={dcHook} onChange={(e) => setDcHook(e.target.value)} placeholder={cfg.discord.webhook_set ? "•••••••• (saved) — paste to replace" : "Server Settings → Integrations → Webhooks"} className="border-line bg-raised font-mono text-sm" data-testid="dc-webhook" />
            <Button onClick={() => saveCfg({ discord: { webhook_url: dcHook, enabled: cfg.discord.enabled } })} disabled={saving} variant="outline" size="sm" className="w-full border-line bg-raised hover:bg-line/40" data-testid="dc-save">Save Discord</Button>
          </Section>

          <Section title="X (Twitter)" desc={cfg.x?.connected ? `Connected as @${cfg.x.handle}. Posts go live.` : cfg.x?.configured ? "Connect the Wen Moon X account to post launches and burns." : "Add X_CLIENT_ID / X_CLIENT_SECRET / X_REDIRECT_URI to the backend .env, then connect."}>
            {cfg.x?.connected ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-dust">Enabled</span>
                  <Switch checked={cfg.x.enabled} onCheckedChange={(v) => saveCfg({ x: { enabled: v } })} data-testid="x-enabled" />
                </div>
                <div className="flex items-center gap-1.5 text-xs text-cream"><Twitter size={13} className="text-juice" /> @{cfg.x.handle}</div>
                <Button onClick={connectX} variant="outline" size="sm" className="w-full border-line bg-raised hover:bg-line/40" data-testid="x-reconnect">Reconnect account</Button>
              </>
            ) : (
              <Button onClick={connectX} variant="outline" size="sm" className="w-full gap-1.5 border-line bg-raised hover:bg-line/40" data-testid="x-connect"><Twitter size={13} /> Connect X account</Button>
            )}
          </Section>

          <Section title="Auto-broadcast" desc="Fire automatically on key events.">
            <div className="flex items-center justify-between">
              <span className="text-sm text-dust">On new launch</span>
              <Switch checked={cfg.auto.on_new_launch} onCheckedChange={(v) => saveCfg({ auto: { on_new_launch: v } })} data-testid="auto-launch" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-dust">On burns over threshold</span>
              <Switch checked={cfg.auto.on_burn_over} onCheckedChange={(v) => saveCfg({ auto: { on_burn_over: v } })} data-testid="auto-burn" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-dust">Threshold</span>
              <div className="flex flex-1 items-center gap-1">
                <span className="text-dust">$</span>
                <Input
                  type="number"
                  defaultValue={cfg.auto.burn_threshold_usd}
                  onBlur={(e) => saveCfg({ auto: { burn_threshold_usd: Number(e.target.value) } })}
                  className="border-line bg-raised font-mono text-sm"
                  data-testid="auto-threshold"
                />
                <span className="text-xs text-dust">USDC</span>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
